// mow-frontend/src/components/analytics/AssetDowntimeWidget.js
// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • Crash fixes: an asset with no name broke search/sort (toLowerCase of null); a response with
//     no data array broke the map. Unnamed assets show as "Asset <id>".
//   • The "AI-Powered Insights" box was fixed rules; it is replaced by the FlowSense AI explainer,
//     which labels the rule findings (critical count, how concentrated downtime is, severity mix)
//     separately from the plain-language AI explanation.
//   • Table rows can be clicked to focus an asset; the chart highlights it.
// --- END UPDATE
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, Spinner, Alert, AlertIcon, Text,
  useColorModeValue, Flex, HStack, VStack, Icon, IconButton,
  Tooltip, Badge, Progress, Stat, StatLabel, StatNumber, StatHelpText,
  SimpleGrid, Input, InputGroup, InputLeftElement, Select,
  useDisclosure, Collapse, Code, Divider, Button, Switch, FormControl,
  FormLabel, Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from '@chakra-ui/react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { 
  MdRefresh, MdSearch, MdFilterList, MdTrendingDown, MdWarning,
  MdCheckCircle, MdTimer, MdInfo, MdDownload, MdBarChart, MdTableChart,
  MdViewModule
} from 'react-icons/md';
import { ChevronDownIcon, ChevronUpIcon, InfoIcon } from '@chakra-ui/icons';
import analyticsService, { asList } from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE
import Card from '../card/Cards';

const AssetDowntimeWidget = () => {
  const [downtimeData, setDowntimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // NEW: Enhanced state for features
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('downtime'); // downtime, name
  const [filterLevel, setFilterLevel] = useState('all'); // all, critical, high, medium, low
  const [viewMode, setViewMode] = useState('chart'); // chart, table, both
  const [showInsights] = useState(true); // --- UPDATE: the explainer replaces the dismissable box
  const [focusName, setFocusName] = useState(null); // --- UPDATE
  
  // Enhanced Theme colors for better light/dark mode support
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const barColor = useColorModeValue('#ED8936', '#F6AD55');
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const iconBoxBg = useColorModeValue('orange.50', 'orange.900');
  
  // NEW: Advanced details disclosure
  const { isOpen: isAdvancedOpen, onToggle: onToggleAdvanced } = useDisclosure();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // NEW: Advanced debugging
        console.log('[DOWNTIME-WIDGET] Starting data fetch...', {
          timestamp: new Date().toISOString(),
          refreshKey
        });
        
        setLoading(true);
        const startTime = performance.now();
        
        const response = await analyticsService.getAssetDowntime();
        
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2);
        
        console.log('[DOWNTIME-WIDGET] Data fetch completed', {
          success: response?.success,
          dataCount: asList(response).length,
          duration: `${fetchDuration}ms`,
          timestamp: new Date().toISOString()
        });
        
        if (response?.success || Array.isArray(response)) {
          // Ensure data is numeric before sorting
          // --- UPDATE: null-safe name + numeric hours (NaN broke sorting and the chart)
          const numericData = asList(response).map((d, i) => ({
              ...d,
              asset_name: d.asset_name || d.name || `Asset ${d.asset_id ?? i + 1}`,
              total_downtime_hours: Number.isFinite(parseFloat(d.total_downtime_hours)) ? parseFloat(d.total_downtime_hours) : 0,
          }));
          // --- END UPDATE
          
          console.log('[DOWNTIME-WIDGET] Data processed', {
            originalCount: numericData.length,
            processedCount: numericData.length,
            sampleData: numericData[0]
          });
          
          numericData.sort((a, b) => b.total_downtime_hours - a.total_downtime_hours);
          setDowntimeData(numericData);
          setError(''); // --- UPDATE: clear an earlier error after a good refresh
          setLastRefresh(new Date());
        } else {
          throw new Error(response?.message || 'Failed to fetch downtime data.');
        }
      } catch (err) {
        console.error('[DOWNTIME-WIDGET] Error fetching data:', {
          error: err.message,
          stack: err.stack,
          timestamp: new Date().toISOString()
        });
        setError(err.message);
      } finally {
        setLoading(false);
        console.log('[DOWNTIME-WIDGET] Fetch cycle complete');
      }
    };
    fetchData();
  }, [refreshKey]);

  // NEW: Helper function to get severity level
  const getSeverityLevel = (hours) => {
    if (hours >= 100) return { level: 'critical', color: 'red', label: 'Critical' };
    if (hours >= 50) return { level: 'high', color: 'orange', label: 'High' };
    if (hours >= 20) return { level: 'medium', color: 'yellow', label: 'Medium' };
    return { level: 'low', color: 'green', label: 'Low' };
  };

  // NEW: Filtered and sorted data
  const filteredData = useMemo(() => {
    console.log('[DOWNTIME-WIDGET] Filtering data', {
      searchTerm,
      sortBy,
      filterLevel,
      originalCount: downtimeData.length
    });
    
    let filtered = [...downtimeData];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        String(item.asset_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply severity filter
    if (filterLevel !== 'all') {
      filtered = filtered.filter(item => {
        const severity = getSeverityLevel(item.total_downtime_hours);
        return severity.level === filterLevel;
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'downtime':
        filtered.sort((a, b) => b.total_downtime_hours - a.total_downtime_hours);
        break;
      case 'name':
        filtered.sort((a, b) => String(a.asset_name || '').localeCompare(String(b.asset_name || '')));
        break;
      default:
        break;
    }
    
    console.log('[DOWNTIME-WIDGET] Filtered results', {
      filteredCount: filtered.length,
      topAsset: filtered[0]?.asset_name
    });
    
    return filtered;
  }, [downtimeData, searchTerm, sortBy, filterLevel]);

  // NEW: Calculate statistics
  const statistics = useMemo(() => {
    if (filteredData.length === 0) return null;
    
    const totalDowntime = filteredData.reduce((sum, item) => sum + item.total_downtime_hours, 0);
    const avgDowntime = totalDowntime / filteredData.length;
    const maxDowntime = Math.max(...filteredData.map(item => item.total_downtime_hours));
    const criticalAssets = filteredData.filter(item => getSeverityLevel(item.total_downtime_hours).level === 'critical').length;
    
    const stats = {
      totalDowntime: totalDowntime.toFixed(1),
      avgDowntime: avgDowntime.toFixed(1),
      maxDowntime: maxDowntime.toFixed(1),
      criticalAssets,
      totalAssets: filteredData.length
    };
    
    console.log('[DOWNTIME-WIDGET] Statistics calculated', stats);
    return stats;
  }, [filteredData]);

  // --- UPDATE: FlowSense rule findings from the real downtime rows
  const dtExplainer = useMemo(() => {
    const findings = [];
    if (!downtimeData.length) return { findings, facts: {} };
    const all = downtimeData;
    const total = all.reduce((s, d) => s + d.total_downtime_hours, 0);
    const crit = all.filter(d => d.total_downtime_hours >= 100);
    const high = all.filter(d => d.total_downtime_hours >= 50 && d.total_downtime_hours < 100);
    const top3 = all.slice(0, 3);
    const top3Share = total > 0 ? (top3.reduce((s, d) => s + d.total_downtime_hours, 0) / total) * 100 : null;
    if (crit.length) findings.push({ severity: 'high', text: `${crit.length} asset${crit.length > 1 ? 's have' : ' has'} 100 h or more of downtime: ${crit.slice(0, 3).map(d => d.asset_name).join(', ')}${crit.length > 3 ? '…' : ''}.` });
    if (high.length) findings.push({ severity: 'medium', text: `${high.length} asset${high.length > 1 ? 's are' : ' is'} between 50 and 100 h.` });
    if (top3Share !== null && all.length > 3) findings.push({ severity: top3Share >= 50 ? 'medium' : 'info', text: `The top 3 assets account for ${top3Share.toFixed(0)}% of all recorded downtime.`, evidence: `${total.toFixed(1)} h across ${all.length} assets` });
    if (!crit.length && !high.length) findings.push({ severity: 'ok', text: 'No asset has 50 h or more of downtime in this window.' });
    return {
      findings,
      facts: {
        window: 'last 90 days (server default)', assets_with_downtime: all.length, total_downtime_hours: +total.toFixed(1),
        critical_assets: crit.length, high_assets: high.length, top3_share_pct: top3Share === null ? null : +top3Share.toFixed(1),
        top_assets: all.slice(0, 10).map(d => ({ name: d.asset_name, hours: +d.total_downtime_hours.toFixed(1), asset_type: d.asset_type ?? null })),
      },
    };
  }, [downtimeData]);
  // --- END UPDATE

  // NEW: Custom bar colors based on severity
  const getBarColor = (value) => {
    const severity = getSeverityLevel(value);
    const colors = {
      critical: '#E53E3E',
      high: '#DD6B20',
      medium: '#D69E2E',
      low: '#38A169'
    };
    return colors[severity.level] || barColor;
  };

  // NEW: Handle data export
  const handleExport = () => {
    console.log('[DOWNTIME-WIDGET] Exporting data', {
      recordCount: filteredData.length,
      timestamp: new Date().toISOString()
    });
    
    const csvContent = [
      ['Asset Name', 'Total Downtime (Hours)', 'Severity Level'],
      ...filteredData.map(item => [
        item.asset_name,
        item.total_downtime_hours.toFixed(2),
        getSeverityLevel(item.total_downtime_hours).label
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `asset-downtime-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('[DOWNTIME-WIDGET] Export completed');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Spinner size="xl" color="orange.500" thickness="4px" />
          <Text mt={4} color="gray.500">Loading downtime data...</Text>
        </Flex>
      );
    }
    
    if (error) {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="bold">Error Loading Data</Text>
            <Text fontSize="sm">{error}</Text>
          </VStack>
        </Alert>
      );
    }
    
    if (downtimeData.length === 0) {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="bold">No Data Available</Text>
            <Text fontSize="sm">No asset downtime has been recorded in the last 90 days.</Text>
          </VStack>
        </Alert>
      );
    }
    
    if (filteredData.length === 0) {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text>No assets match your current filters.</Text>
        </Alert>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {/* NEW: Statistics Dashboard */}
        {statistics && (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Total Downtime</StatLabel>
              <StatNumber fontSize="2xl">{statistics.totalDowntime}h</StatNumber>
              <StatHelpText>Last 90 days</StatHelpText>
            </Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Average Downtime</StatLabel>
              <StatNumber fontSize="2xl">{statistics.avgDowntime}h</StatNumber>
              <StatHelpText>Per asset</StatHelpText>
            </Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Highest Downtime</StatLabel>
              <StatNumber fontSize="2xl">{statistics.maxDowntime}h</StatNumber>
              <StatHelpText>{filteredData[0]?.asset_name}</StatHelpText>
            </Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Critical Assets</StatLabel>
              <StatNumber fontSize="2xl" color="red.500">{statistics.criticalAssets}</StatNumber>
              <StatHelpText>≥100 hours</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* NEW: Search and Filter Controls */}
        <Flex gap={3} wrap="wrap" align="center" justify="space-between">
          <HStack spacing={3} flex="1">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <MdSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
              />
            </InputGroup>
            
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              size="sm"
              maxW="150px"
            >
              <option value="downtime">By Downtime</option>
              <option value="name">By Name</option>
            </Select>
            
            <Select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              size="sm"
              maxW="150px"
            >
              <option value="all">All Levels</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
            
            <Badge colorScheme="purple">
              {filteredData.length} assets
            </Badge>
            {focusName && (
              <Badge colorScheme="orange" cursor="pointer" onClick={() => setFocusName(null)}>
                {focusName} ✕
              </Badge>
            )}
          </HStack>

          {/* NEW: View Mode Controls */}
          <HStack spacing={2}>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="view-mode" mb="0" fontSize="sm" mr={2}>
                View:
              </FormLabel>
              <HStack spacing={1}>
                <Tooltip label="Chart View" hasArrow>
                  <IconButton
                    icon={<MdBarChart />}
                    size="sm"
                    variant={viewMode === 'chart' ? 'solid' : 'ghost'}
                    colorScheme={viewMode === 'chart' ? 'orange' : 'gray'}
                    onClick={() => setViewMode('chart')}
                    aria-label="Chart view"
                  />
                </Tooltip>
                <Tooltip label="Table View" hasArrow>
                  <IconButton
                    icon={<MdTableChart />}
                    size="sm"
                    variant={viewMode === 'table' ? 'solid' : 'ghost'}
                    colorScheme={viewMode === 'table' ? 'orange' : 'gray'}
                    onClick={() => setViewMode('table')}
                    aria-label="Table view"
                  />
                </Tooltip>
                <Tooltip label="Both Views" hasArrow>
                  <IconButton
                    icon={<MdViewModule />}
                    size="sm"
                    variant={viewMode === 'both' ? 'solid' : 'ghost'}
                    colorScheme={viewMode === 'both' ? 'orange' : 'gray'}
                    onClick={() => setViewMode('both')}
                    aria-label="Both views"
                  />
                </Tooltip>
              </HStack>
            </FormControl>
          </HStack>
        </Flex>

        {/* --- UPDATE: FlowSense AI explainer (was a fixed "AI-Powered Insights" list) */}
        {showInsights && statistics && (
          <FlowSenseAIExplainer
            module="asset_downtime"
            title="Explain downtime"
            subtitle="Which assets are out of service longest, and why it matters"
            facts={dtExplainer.facts}
            findings={dtExplainer.findings}
            questions={[
              'Which assets should get preventive maintenance first?',
              'Is downtime concentrated in one asset type?',
              'What is the likely cost of this downtime?',
            ]}
          />
        )}
        {/* --- END UPDATE */}

        {/* Chart View */}
        {(viewMode === 'chart' || viewMode === 'both') && (
          <Box>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={filteredData.slice(0, 10)} 
                layout="vertical" 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit="h" />
                <YAxis type="category" dataKey="asset_name" width={150} />
                <RechartsTooltip 
                  formatter={(value) => `${Number(value).toFixed(1)} hours`}
                  contentStyle={{ 
                    backgroundColor: bgColor, 
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="total_downtime_hours" name="Total Downtime (Hours)">
                  {filteredData.slice(0, 10).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.total_downtime_hours)}
                      fillOpacity={focusName && focusName !== entry.asset_name ? 0.35 : 1}
                      cursor="pointer"
                      onClick={() => setFocusName(prev => (prev === entry.asset_name ? null : entry.asset_name))}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* NEW: Table View */}
        {(viewMode === 'table' || viewMode === 'both') && (
          <Box mt={viewMode === 'both' ? 6 : 0}>
            <TableContainer borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
              <Table variant="simple" size="sm">
                <Thead bg={statBg}>
                  <Tr>
                    <Th>Rank</Th>
                    <Th>Asset Name</Th>
                    <Th isNumeric>Downtime (Hours)</Th>
                    <Th>Severity</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredData.slice(0, 15).map((asset, index) => {
                    const severity = getSeverityLevel(asset.total_downtime_hours);
                    return (
                      <Tr
                        key={asset.asset_id ?? index}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        bg={focusName === asset.asset_name ? hoverBg : undefined}
                        onClick={() => setFocusName(prev => (prev === asset.asset_name ? null : asset.asset_name))}
                      >
                        <Td>
                          <Badge colorScheme="gray" variant="subtle">
                            #{index + 1}
                          </Badge>
                        </Td>
                        <Td fontWeight="medium">{asset.asset_name}</Td>
                        <Td isNumeric fontSize="md" fontWeight="bold" color={`${severity.color}.500`}>
                          {asset.total_downtime_hours.toFixed(1)}h
                        </Td>
                        <Td>
                          <Badge colorScheme={severity.color}>
                            {severity.label}
                          </Badge>
                        </Td>
                        <Td>
                          <Progress 
                            value={Number(statistics.maxDowntime) > 0 ? Math.min((asset.total_downtime_hours / Number(statistics.maxDowntime)) * 100, 100) : 0} 
                            colorScheme={severity.color}
                            size="sm"
                            borderRadius="full"
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* NEW: Severity Legend */}
        <HStack spacing={4} justify="center" fontSize="sm">
          <HStack>
            <Box w="12px" h="12px" bg="red.500" borderRadius="sm" />
            <Text>Critical (≥100h)</Text>
          </HStack>
          <HStack>
            <Box w="12px" h="12px" bg="orange.500" borderRadius="sm" />
            <Text>High (50-100h)</Text>
          </HStack>
          <HStack>
            <Box w="12px" h="12px" bg="yellow.500" borderRadius="sm" />
            <Text>Medium (20-50h)</Text>
          </HStack>
          <HStack>
            <Box w="12px" h="12px" bg="green.500" borderRadius="sm" />
            <Text>Low (&lt;20h)</Text>
          </HStack>
        </HStack>
      </VStack>
    );
  };

  return (
    <Box 
      bg={cardBg} 
      borderRadius="xl" 
      boxShadow="xl"
      p={6}
      borderWidth="1px"
      borderColor={borderColor}
      position="relative"
      overflow="hidden"
      _hover={{
        boxShadow: '2xl',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease'
      }}
      transition="all 0.3s ease"
    >
      {/* Decorative gradient overlay */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="4px"
        bgGradient="linear(to-r, orange.400, red.400)"
        opacity="0.8"
      />
      
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={3}>
          <Box
            p={2}
            bg={iconBoxBg}
            borderRadius="lg"
          >
            <Icon as={MdTrendingDown} boxSize={6} color="orange.500" />
          </Box>
          <VStack align="flex-start" spacing={0}>
            <Heading size="md" color={headingColor}>Top Assets by Downtime</Heading>
            <HStack spacing={2} mt={1}>
              {lastRefresh && (
                <Tooltip label={`Last updated: ${lastRefresh.toLocaleString()}`} hasArrow>
                  <Badge colorScheme="blue" cursor="help" fontSize="xs">
                    {Math.floor((new Date() - lastRefresh) / 60000)}m ago
                  </Badge>
                </Tooltip>
              )}
              <Badge colorScheme="orange" fontSize="xs">Last 90 days</Badge>
            </HStack>
          </VStack>
        </HStack>
        
        <HStack spacing={2}>
          <Tooltip label="Export to CSV" hasArrow>
            <IconButton
              icon={<MdDownload />}
              size="sm"
              variant="ghost"
              onClick={handleExport}
              isDisabled={!downtimeData.length}
              aria-label="Export data"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
          <Tooltip label="Refresh Data" hasArrow>
            <IconButton
              icon={<MdRefresh />}
              size="sm"
              variant="ghost"
              onClick={() => setRefreshKey(prev => prev + 1)}
              isLoading={loading}
              aria-label="Refresh"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
        </HStack>
      </Flex>

      <Text fontSize="sm" color={mutedColor} mb={4}>
        Analyzing asset downtime over the last 90 days to identify reliability issues
      </Text>

      {renderContent()}

      {/* NEW: Advanced Details Section */}
      {!loading && !error && downtimeData.length > 0 && (
        <Box mt={4}>
          <Button
            size="sm"
            variant="ghost"
            rightIcon={isAdvancedOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={onToggleAdvanced}
          >
            Advanced Details
          </Button>
          <Collapse in={isAdvancedOpen} animateOpacity>
            <Box mt={3} p={4} bg={statBg} borderRadius="md" fontSize="sm">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Data Information</Text>
                  <VStack align="flex-start" spacing={1}>
                    <Code fontSize="xs">Total Records: {downtimeData.length}</Code>
                    <Code fontSize="xs">Filtered Records: {filteredData.length}</Code>
                    <Code fontSize="xs">Time Period: Last 90 days</Code>
                    <Code fontSize="xs">Last Refresh: {lastRefresh?.toLocaleString() || 'N/A'}</Code>
                  </VStack>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Severity Distribution</Text>
                  <VStack align="flex-start" spacing={1}>
                    <HStack>
                      <Badge colorScheme="red">Critical</Badge>
                      <Text fontSize="xs">{downtimeData.filter(d => getSeverityLevel(d.total_downtime_hours).level === 'critical').length} assets</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="orange">High</Badge>
                      <Text fontSize="xs">{downtimeData.filter(d => getSeverityLevel(d.total_downtime_hours).level === 'high').length} assets</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="yellow">Medium</Badge>
                      <Text fontSize="xs">{downtimeData.filter(d => getSeverityLevel(d.total_downtime_hours).level === 'medium').length} assets</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="green">Low</Badge>
                      <Text fontSize="xs">{downtimeData.filter(d => getSeverityLevel(d.total_downtime_hours).level === 'low').length} assets</Text>
                    </HStack>
                  </VStack>
                </Box>
              </SimpleGrid>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default AssetDowntimeWidget;